import api from './api';
import type {
    DocumentChecklist,
    ApplicationDocument,
    TimelineResponse,
    ApplicationProgress,
    NotificationListResponse,
    JourneyStepsResponse,
    JourneyOverviewResponse,
    JourneyStep
} from '../types';

class TrackingService {
    /**
     * Get document checklist for an application
     */
    async getChecklist(applicationId: number): Promise<DocumentChecklist> {
        const response = await api.get<DocumentChecklist>(`/tracking/my-applications/${applicationId}/documents`);
        return response.data;
    }

    /**
     * Attach an uploaded file to a document requirement
     */
    async uploadDocument(
        applicationId: number,
        documentId: number,
        fileData: {
            document_type: string;
            file_url: string;
            file_name: string;
            file_size: number;
            file_type: string;
        }
    ): Promise<ApplicationDocument> {
        const response = await api.post<ApplicationDocument>(
            `/tracking/my-applications/${applicationId}/documents/${documentId}/upload`,
            fileData
        );
        return response.data;
    }

    /**
     * Add a custom document to the application (Student only)
     * Allows students to add their own documents without waiting for consultant request
     */
    async addCustomDocument(
        applicationId: number,
        data: {
            document_type: string;
            document_name: string;
            file_url: string;
            file_name: string;
            file_size: number;
            file_type: string;
            notes?: string;
        }
    ): Promise<ApplicationDocument> {
        const response = await api.post<ApplicationDocument>(
            `/tracking/my-applications/${applicationId}/documents/add`,
            {
                ...data,
                document_name_ar: data.document_name // Use same name for both
            }
        );
        return response.data;
    }

    /**
     * Get application timeline
     */
    async getTimeline(applicationId: number): Promise<TimelineResponse> {
        const response = await api.get<TimelineResponse>(`/tracking/my-applications/${applicationId}/timeline`);
        return response.data;
    }

    /**
     * Get overall application progress
     */
    async getProgress(applicationId: number): Promise<ApplicationProgress> {
        const response = await api.get<ApplicationProgress>(`/tracking/my-applications/${applicationId}/progress`);
        return response.data;
    }

    /**
     * Get notifications
     */
    async getNotifications(params?: { unread_only?: boolean; skip?: number; limit?: number }): Promise<NotificationListResponse> {
        const response = await api.get<NotificationListResponse>('/tracking/notifications', { params });
        return response.data;
    }

    /**
     * Mark notifications as read
     */
    async markRead(notificationIds: number[]): Promise<void> {
        await api.post('/tracking/notifications/mark-read', { notification_ids: notificationIds });
    }

    /**
     * Mark all notifications as read
     */
    async markAllRead(): Promise<void> {
        await api.post('/tracking/notifications/mark-all-read');
    }

    /**
     * Get journey steps for a specific application
     * Retrieves all visible journey steps for the authenticated student
     */
    async getJourneySteps(applicationId: number): Promise<JourneyStepsResponse> {
        const response = await api.get<JourneyStepsResponse>(`/journey/my-applications/${applicationId}/steps`);
        return response.data;
    }

    /**
     * Get journey overview for all student applications
     * Retrieves an overview of the student's journey across all applications
     */
    async getJourneyOverview(): Promise<JourneyOverviewResponse> {
        const response = await api.get<JourneyOverviewResponse>('/journey/my-journey-overview');
        return response.data;
    }

    /**
     * Update journey step status for student's own application
     * Students can toggle between pending and completed status
     */
    async updateJourneyStepStatus(
        applicationId: number,
        stepId: number,
        status: 'pending' | 'completed'
    ): Promise<JourneyStep> {
        const response = await api.put<JourneyStep>(
            `/journey/my-applications/${applicationId}/steps/${stepId}`,
            { status }
        );
        return response.data;
    }
}

export default new TrackingService();
